/**
 * Rook Components — Shoppable Video Carousel
 * Web Component: <rk-video-carousel>
 *
 * - Vídeos inline com autoplay mudo, carregados sob demanda (IntersectionObserver).
 *   O poster (frame do vídeo) aparece de imediato; a <source> só é injetada quando
 *   o slide entra na viewport, então nenhum byte de vídeo carrega antes disso.
 * - Clicar no vídeo abre o modal fullscreen, cujos slides são montados a partir dos
 *   data-attributes de cada item (mesma estratégia lazy).
 */
if (!customElements.get('rk-video-carousel')) {
  const CART_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="18" height="18">' +
    '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>' +
    '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>';

  const escapeHtml = (str) =>
    String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  class RkVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this._modalMuted = true;
      this._activeIndex = -1;
      this._built = false;
      this._swiper = null;
      this._modalVideos = [];
      this._io = null;
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onCarouselInit = this._onCarouselInit.bind(this);
    }

    connectedCallback() {
      this._blockId = this.dataset.blockId || 'default';
      this._modalId = 'rk-sv-modal-' + this._blockId;

      this._items = [...this.querySelectorAll('.rk-sv__item')];
      this._items.forEach((item, i) => (item.dataset.videoIndex = i));

      this._setupInline();

      // Move o modal para o <body> para escapar de contextos de empilhamento.
      this._modalEl = this.querySelector('rk-modal-element');
      if (this._modalEl) document.body.appendChild(this._modalEl);

      this._modalDiv = this._modalEl?.querySelector('.rk-modal');
      this._stage = this._modalEl?.querySelector('.rk-sv-modal__stage');
      this._productRow = this._modalEl?.querySelector('.rk-sv-modal__product-row');
      this._modalCarousel = this._modalEl?.querySelector('.rk-sv-modal__carousel');

      if (this._modalCarousel) {
        if (this._modalCarousel.swiper) this._onReady(this._modalCarousel.swiper);
        this._modalCarousel.addEventListener('rk:carousel:init', this._onCarouselInit);
      }

      this._bindModalControls();
    }

    disconnectedCallback() {
      this._io?.disconnect();
      this._io = null;
      document.removeEventListener('keydown', this._onKeyDown);
      this._modalCarousel?.removeEventListener('rk:carousel:init', this._onCarouselInit);
      if (this._modalEl?.parentNode === document.body) {
        document.body.removeChild(this._modalEl);
      }
    }

    /* ============ Vídeos inline (lazy + autoplay) ============ */
    _setupInline() {
      this._io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const item = entry.target;
            const video = item.querySelector('.rk-sv__video');
            if (!video) continue;
            if (entry.isIntersecting) {
              this._lazyLoad(video);
              this._playInline(item, video);
            } else {
              video.pause();
            }
          }
        },
        { threshold: 0.5 }
      );

      this._items.forEach((item) => {
        const video = item.querySelector('.rk-sv__video');

        if (video) {
          video.addEventListener('play', () => item.classList.add('is-playing'));
          video.addEventListener('pause', () => item.classList.remove('is-playing'));
          this._io.observe(item);
        }

        item.querySelector('.rk-sv__mute-btn')?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._toggleInlineMute(item, video);
        });

        item.querySelector('.rk-sv__play-btn')?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._toggleInlinePlay(item, video);
        });

        // Clicar no vídeo abre o modal (o card de produto é link próprio).
        item.addEventListener('click', (e) => {
          if (e.target.closest('.rk-sv__card') || e.target.closest('button')) return;
          this._open(parseInt(item.dataset.videoIndex, 10) || 0);
        });
      });
    }

    _lazyLoad(video) {
      if (video.dataset.loaded) return;
      const src = video.dataset.src;
      if (src) {
        const source = document.createElement('source');
        source.src = src;
        source.type = 'video/mp4';
        video.appendChild(source);
        video.load();
      }
      video.dataset.loaded = '1';
    }

    _playInline(item, video) {
      video.muted = item.classList.contains('is-muted');
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          video.muted = true;
          item.classList.add('is-muted');
          video.play().catch(() => {});
        });
      }
    }

    _toggleInlineMute(item, video) {
      if (!video) return;
      const muted = !video.muted;
      video.muted = muted;
      item.classList.toggle('is-muted', muted);
    }

    _toggleInlinePlay(item, video) {
      if (!video) return;
      this._lazyLoad(video);
      if (video.paused) {
        this._playInline(item, video);
      } else {
        video.pause();
      }
    }

    _inView(el) {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    }

    /* ============ Modal (viewer fullscreen) ============ */
    _onCarouselInit(e) {
      if (e.detail?.swiper) this._onReady(e.detail.swiper);
    }

    _onReady(swiper) {
      if (this._swiper === swiper) return;
      this._swiper = swiper;
      this._swiper.on('slideChange', () => this._activate(this._swiper.activeIndex));
      if (this._built && this._activeIndex >= 0) {
        this._swiper.update();
        this._swiper.slideTo(this._activeIndex, 0);
      }
    }

    _build() {
      if (this._built) return;
      this._built = true;

      const wrapper = this._modalCarousel?.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      this._items.forEach((item) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide rk-carousel__slide';

        const video = document.createElement('video');
        video.className = 'rk-sv-modal__video';
        video.loop = true;
        video.playsInline = true;
        video.muted = this._modalMuted;
        video.preload = 'none';
        if (item.dataset.poster) video.poster = item.dataset.poster;
        if (item.dataset.videoSrc) video.dataset.src = item.dataset.videoSrc;

        slide.appendChild(video);
        wrapper.appendChild(slide);
      });

      this._modalVideos = [...wrapper.querySelectorAll('.rk-sv-modal__video')];
    }

    _open(index) {
      this._build();

      // Pausa todos os vídeos inline para não sobrepor áudio.
      this._items.forEach((it) => it.querySelector('.rk-sv__video')?.pause());

      this._modalDiv.classList.add('rk-modal--active');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeyDown);
      this._modalEl.dispatchEvent(
        new CustomEvent('rk:modal:open', { bubbles: true, detail: { id: this._modalId, type: 'custom' } })
      );
      this._afterOpen(index);
    }

    _afterOpen(index) {
      const init = () => {
        if (this._swiper) {
          this._swiper.update();
          this._swiper.slideTo(index, 0);
          this._swiper.update();
        }
        this._activate(index);
      };
      const onEnd = (e) => {
        if (e.target !== this._modalDiv) return;
        this._modalDiv.removeEventListener('transitionend', onEnd);
        init();
      };
      this._modalDiv.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        this._modalDiv.removeEventListener('transitionend', onEnd);
        init();
      }, 350);
    }

    _close() {
      this._deactivate();
      this._activeIndex = -1;
      document.removeEventListener('keydown', this._onKeyDown);
      this._modalDiv.classList.remove('rk-modal--active');
      document.body.style.overflow = '';
      this._modalEl.dispatchEvent(
        new CustomEvent('rk:modal:close', { bubbles: true, detail: { id: this._modalId, type: 'custom' } })
      );
      // Retoma os vídeos inline visíveis.
      this._items.forEach((it) => {
        const v = it.querySelector('.rk-sv__video');
        if (v && this._inView(it)) this._playInline(it, v);
      });
    }

    _activate(index) {
      if (index === this._activeIndex) return;
      this._deactivate();
      this._activeIndex = index;

      const video = this._modalVideos[index];
      if (video) {
        if (!video.dataset.loaded && video.dataset.src) {
          const source = document.createElement('source');
          source.src = video.dataset.src;
          source.type = 'video/mp4';
          video.appendChild(source);
          video.dataset.loaded = '1';
        }
        video.muted = this._modalMuted;
        try { video.currentTime = 0; } catch (e) { /* noop */ }
        if (video.readyState < 2) video.load();
        video.play().catch(() => {
          video.muted = true;
          this._modalMuted = true;
          this._syncModalMute();
          video.play().catch(() => {});
        });
      }

      this._swapProduct(index);
    }

    _deactivate() {
      const video = this._modalVideos[this._activeIndex];
      if (video) video.pause();
    }

    _swapProduct(index) {
      if (!this._productRow) return;
      const item = this._items[index];
      const url = item?.dataset.productUrl;
      if (!url) {
        this._productRow.innerHTML = '';
        return;
      }

      const title = escapeHtml(item.dataset.productTitle);
      const price = escapeHtml(item.dataset.productPrice);
      const img = item.dataset.productImage;
      const imgAlt = escapeHtml(item.dataset.productImageAlt || item.dataset.productTitle);

      const imgHtml = img
        ? `<img class="rk-sv-modal__product-img" src="${escapeHtml(img)}" alt="${imgAlt}" loading="lazy">`
        : '';

      this._productRow.innerHTML =
        `<a href="${escapeHtml(url)}" class="rk-sv-modal__product">` +
        '<div class="rk-sv-modal__product-inner">' +
        imgHtml +
        '<div class="rk-sv-modal__product-info">' +
        `<p class="rk-sv-modal__product-title">${title}</p>` +
        `<p class="rk-sv-modal__product-price">${price}</p>` +
        '</div>' +
        `<div class="rk-sv-modal__product-action">${CART_SVG}</div>` +
        '</div></a>';
    }

    /* ============ Controles do modal ============ */
    _bindModalControls() {
      const q = (s) => this._modalEl?.querySelector(s);
      q('.rk-sv-modal__close')?.addEventListener('click', () => this._close());
      q('.rk-modal__overlay')?.addEventListener('click', () => this._close());
      q('.rk-sv-modal__mute')?.addEventListener('click', () => this._toggleModalMute());
      q('[data-tap="prev"]')?.addEventListener('click', () => this._prev());
      q('[data-tap="next"]')?.addEventListener('click', () => this._next());
    }

    _toggleModalMute() {
      this._modalMuted = !this._modalMuted;
      const video = this._modalVideos[this._activeIndex];
      if (video) video.muted = this._modalMuted;
      this._syncModalMute();
    }

    _syncModalMute() {
      this._stage?.classList.toggle('is-unmuted', !this._modalMuted);
    }

    _prev() {
      if (this._activeIndex <= 0) return;
      this._swiper?.slidePrev();
    }

    _next() {
      if (this._activeIndex >= this._modalVideos.length - 1) {
        this._close();
        return;
      }
      this._swiper?.slideNext();
    }

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this._close();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._next();
      } else if (e.key === 'm') {
        e.preventDefault();
        this._toggleModalMute();
      }
    }
  }

  customElements.define('rk-video-carousel', RkVideoCarousel);
}
